import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const CardiacTechnologyCoursePage: React.FC = () => {
  const cardiacData = {
    title: "Bachelor of Science in Cardiac Technology",
    shortName: "B.Sc CT",
    duration: "3 Years",
    description: "A specialized healthcare program focusing on cardiac diagnostics, cardiovascular procedures, and heart-related medical technology and patient care.",
    overview: "B.Sc Cardiac Technology prepares students to become cardiac technologists who perform various cardiac diagnostic procedures and assist in cardiovascular treatments. The program covers cardiovascular anatomy, cardiac catheterization, echocardiography, ECG interpretation, and cardiac monitoring. Students learn to operate sophisticated cardiac equipment, assist in cardiac surgeries, and provide specialized cardiac care in various healthcare settings.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Interest in cardiology and medical technology",
      "Good hand-eye coordination",
      "Ability to work under pressure"
    ],
    careerProspects: {
      roles: [
        "Cardiac Technologist",
        "ECG Technician",
        "Echocardiography Technologist",
        "Cardiac Catheterization Technologist",
        "Cardiovascular Equipment Specialist",
        "Cardiac Monitor Technician",
        "Pacemaker Technician",
        "Cardiac Rehabilitation Specialist",
        "Perfusionist Assistant",
        "Cardiovascular Research Coordinator"
      ],
      industries: [
        "Cardiac Hospitals",
        "Cardiothoracic Surgery Centers",
        "Cardiac Catheterization Labs",
        "ICU and CCU Units",
        "Cardiac Rehabilitation Centers",
        "Medical Equipment Companies",
        "Research Institutions",
        "Government Hospitals",
        "Private Cardiology Clinics",
        "Emergency Medical Services"
      ],
      averageSalary: "₹3.5-9 LPA (varies by experience and specialization)",
      topRecruiters: [
        "Fortis Escorts Heart Institute",
        "Apollo Hospitals",
        "Narayana Health",
        "Medanta Heart Institute",
        "Max Healthcare",
        "AIIMS Cardiology",
        "Manipal Hospitals",
        "Asian Heart Institute",
        "GE Healthcare",
        "Philips Healthcare"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy and Physiology",
          "Cardiovascular Anatomy",
          "Basic Sciences",
          "Medical Electronics",
          "Medical Terminology",
          "Patient Care",
          "Communication Skills",
          "Introduction to Cardiology"
        ]
      },
      {
        year: 2,
        subjects: [
          "Electrocardiography (ECG)",
          "Echocardiography",
          "Cardiac Pathophysiology",
          "Cardiac Medications",
          "Cardiac Monitoring",
          "Stress Testing",
          "Holter Monitoring",
          "Cardiac Equipment"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Cardiac Procedures",
          "Cardiac Catheterization",
          "Pacemaker Technology",
          "Cardiac Surgery Assistance",
          "Cardiac Rehabilitation",
          "Research Methods",
          "Professional Practice",
          "Clinical Internship"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Cardiac Technology",
      "Entrance examination (typically NEET-based)",
      "Merit-based selection",
      "Document verification",
      "Medical fitness certificate",
      "Fee payment and admission",
      "Orientation program",
      "Begin clinical and theoretical training"
    ],
    topColleges: [
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹15,000 PA",
        ranking: "Premier Medical Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹3 LPA",
        ranking: "Premier Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹4 LPA",
        ranking: "Deemed University"
      },
      {
        name: "JSS Academy of Higher Education",
        location: "Mysore, Karnataka",
        fees: "₹2.8 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Government Medical College",
        location: "Chandigarh",
        fees: "₹50,000 PA",
        ranking: "Government Medical College"
      }
    ],
    skills: [
      "Equipment Operation",
      "ECG Interpretation",
      "Patient Care",
      "Technical Proficiency",
      "Attention to Detail",
      "Emergency Response",
      "Communication Skills",
      "Hand-Eye Coordination",
      "Problem Solving",
      "Teamwork",
      "Continuous Learning",
      "Stress Management"
    ],
    futureScope: "Cardiac technology is advancing rapidly with artificial intelligence in ECG interpretation, advanced imaging techniques, minimally invasive procedures, and robotic cardiac surgery assistance. Future opportunities include specialization in electrophysiology, structural heart procedures, artificial heart technology, and telemedicine cardiac monitoring. The increasing prevalence of heart disease ensures strong job growth in this field.",
    faqs: [
      {
        question: "What procedures do cardiac technologists perform?",
        answer: "Cardiac technologists perform ECGs, echocardiograms, stress tests, Holter monitoring, assist in cardiac catheterization procedures, operate cardiac monitoring equipment, and help with pacemaker implantations."
      },
      {
        question: "Is this field suitable for those afraid of blood or medical procedures?",
        answer: "Cardiac technology involves working with patients during medical procedures, including some that may involve blood. Students should be comfortable with medical environments and patient care scenarios."
      },
      {
        question: "What is the difference between cardiac technology and cardiovascular technology?",
        answer: "The terms are often used interchangeably, but cardiovascular technology may include broader vascular procedures beyond just the heart, while cardiac technology focuses specifically on heart-related procedures."
      },
      {
        question: "Can I work in cardiac surgery as a technologist?",
        answer: "Yes, cardiac technologists can work in cardiac surgery as perfusionist assistants, monitoring specialists, or equipment technicians, though some roles may require additional specialized training."
      },
      {
        question: "What are the advancement opportunities?",
        answer: "Career advancement includes becoming senior technologists, supervisors, department managers, clinical educators, or specializing in advanced procedures like electrophysiology or interventional cardiology support."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {cardiacData.title} ({cardiacData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={cardiacData} />
      </div>
    </div>
  );
};

export default CardiacTechnologyCoursePage;